/** Coordinates auth provider and token handling; implements the shell-facing auth port. */
import type { AuthApplicationPort, AuthUser } from '../domain/ports/AuthApplicationPort.ts';
import type { AuthProviderPort } from '../domain/ports/AuthProviderPort.ts';
import type { AuthTokenStoragePort } from '../domain/ports/AuthTokenStoragePort.ts';

function mapAuthUser(user: unknown): AuthUser | null {
  if (!user || typeof user !== 'object') return null;
  const u = user as Record<string, unknown>;
  const raw = u.id;
  const idStr =
    raw != null &&
    raw !== '' &&
    (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'bigint')
      ? String(raw)
      : '';
  if (!idStr) return null;
  const permissionsRaw = u.permissions;
  const permissions =
    Array.isArray(permissionsRaw) && permissionsRaw.length > 0
      ? permissionsRaw.filter((x): x is string => typeof x === 'string')
      : undefined;
  const emailStr = typeof u.email === 'string' ? u.email.trim() : '';
  const nombrePersona = typeof u.nombre === 'string' ? u.nombre.trim() : '';
  const nombreUsuario = typeof u.nombre_usuario === 'string' ? u.nombre_usuario.trim() : '';
  const displayNameRaw =
    nombreUsuario ||
    (typeof u.displayName === 'string' ? u.displayName.trim() : '') ||
    (typeof u.name === 'string' ? u.name.trim() : '') ||
    (typeof u.fullName === 'string' ? u.fullName.trim() : '') ||
    emailStr ||
    '';
  const publicIdRaw =
    (typeof u.publicId === 'string' && u.publicId.trim()) ||
    (typeof u.public_id === 'string' && u.public_id.trim()) ||
    '';
  return {
    id: idStr,
    publicId: publicIdRaw || undefined,
    email: emailStr || undefined,
    nombre: nombrePersona || undefined,
    displayName: displayNameRaw || undefined,
    role: typeof u.role === 'string' ? u.role : undefined,
    permissions: permissions?.length ? permissions : undefined,
  };
}

export class AuthService implements AuthApplicationPort {
  private _isAuthenticated = false;
  private _currentUser: unknown = null;

  /** Constructs the authentication service. */
  constructor(
    private readonly authProvider: AuthProviderPort,
    private readonly tokenStorage: AuthTokenStoragePort | null = null,
  ) {}

  /** Initializes the user session. */
  async initializeSession(): Promise<void> {
    try {
      const session = await this.authProvider.getSession();
      this._isAuthenticated = !!session.accessToken;
      this._currentUser = session.user ?? null;

      if (this.tokenStorage && session.accessToken) {
        try {
          this.tokenStorage.setToken(session.accessToken);
        } catch {
          this._isAuthenticated = false;
          this._currentUser = null;
        }
      }
    } catch {
      this._isAuthenticated = false;
      this._currentUser = null;
    }
  }

  /** Logs in a user with email or username and password. */
  async login(usernameOrEmail: string, password: string): Promise<void> {
    const { accessToken, user } = await this.authProvider.login(usernameOrEmail, password);

    if (this.tokenStorage && accessToken) {
      try {
        this.tokenStorage.setToken(accessToken);
      } catch {
        this._isAuthenticated = false;
        this._currentUser = null;
        throw new Error('No se pudo guardar la sesión en el dispositivo.');
      }
    }

    this._isAuthenticated = true;
    this._currentUser = user ?? null;
  }

  /** Logs out the current user. */
  async logout(): Promise<void> {
    try {
      await this.authProvider.logout();
    } catch {
      /* Remote sign-out is best-effort (network / 401 handled in the adapter). */
    }

    if (this.tokenStorage) {
      try {
        this.tokenStorage.clearToken();
      } catch {
        /* Storage may fail; local state is still reset. */
      }
    }

    this._isAuthenticated = false;
    this._currentUser = null;
  }

  /** Checks if the user is authenticated. */
  async isAuthenticated(): Promise<boolean> {
    try {
      if (this._isAuthenticated && this.tokenStorage?.getToken()?.trim()) {
        return true;
      }
      this._isAuthenticated = await this.authProvider.isAuthenticated();
      return this._isAuthenticated;
    } catch {
      return false;
    }
  }

  /** Retrieves the current user object. */
  async getCurrentUser(): Promise<AuthUser | null> {
    if (!this._currentUser) {
      try {
        this._currentUser = await this.authProvider.getUser();
      } catch {
        this._currentUser = null;
      }
    }
    return mapAuthUser(this._currentUser);
  }
}
